/** Invoice module that supports FMS/FleetOS send retry via /api/jobs/sendinvoice. */
export const INTEGRATION_INVOICE_MODULE = 5;

export type IntegrationRetryTarget = { kind: "invoice"; id: number };

const FAILED_STATUS = 2;
const SUCCESS_STATUS = 1;

const toPositiveId = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const normalizeInvoiceNo = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/** Same invoiceNo + jobId group key used to decide retry visibility. */
export const getInvoiceRetryGroupKey = (row: any): string | null => {
  if (!row || Number(row?.module) !== INTEGRATION_INVOICE_MODULE) return null;
  const req = row?.requestPayload || {};
  const invoiceNo = normalizeInvoiceNo(
    req.invoiceNo ?? req.invoiceNumber ?? req?.payload?.invoiceNumber,
  );
  const jobId = toPositiveId(req.jobId);
  if (!invoiceNo || !jobId) return null;
  return `${invoiceNo}::${jobId}`;
};

/**
 * Resolve invoiceId for retry POST /api/jobs/sendinvoice/{invoiceId}.
 * Only for failed invoice rows with a resolvable invoiceId.
 */
export const getIntegrationRetryTarget = (
  row: any,
): IntegrationRetryTarget | null => {
  if (!row) return null;
  if (Number(row?.module) !== INTEGRATION_INVOICE_MODULE) return null;
  if (Number(row?.status ?? row?.integrationStatus) !== FAILED_STATUS)
    return null;

  const req = row?.requestPayload || {};
  const id = toPositiveId(req.invoiceId);
  return id ? { kind: "invoice", id } : null;
};

/**
 * Show Retry only for invoices when:
 * - row is failed
 * - invoiceNo + jobId can be resolved
 * - every log on the page with the same invoiceNo + jobId is failed
 *   (if any peer succeeded, hide Retry on all of them)
 * - among that all-failed group, only the latest row shows the button
 */
export const shouldShowIntegrationRetry = (
  row: any,
  allRows: any[] = [],
): boolean => {
  const target = getIntegrationRetryTarget(row);
  if (!target) return false;

  const groupKey = getInvoiceRetryGroupKey(row);
  if (!groupKey) return false;

  const peers = (allRows || []).filter(
    (r) => getInvoiceRetryGroupKey(r) === groupKey,
  );

  if (!peers.length) return false;

  const anySuccess = peers.some(
    (r) => Number(r?.status ?? r?.integrationStatus) === SUCCESS_STATUS,
  );
  if (anySuccess) return false;

  const allFailed = peers.every(
    (r) => Number(r?.status ?? r?.integrationStatus) === FAILED_STATUS,
  );
  if (!allFailed) return false;

  const latest = peers.reduce((best, cur) => {
    const bestTime = new Date(best?.createdAt || 0).getTime();
    const curTime = new Date(cur?.createdAt || 0).getTime();
    if (curTime !== bestTime) return curTime > bestTime ? cur : best;
    return Number(cur?.id || 0) > Number(best?.id || 0) ? cur : best;
  }, peers[0]);

  return Number(latest?.id) === Number(row?.id);
};
